import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * ClockWidget - Employee clock in/out component
 * 
 * Features:
 * - Clock in/out with one tap
 * - GPS location verification (if enabled)
 * - Break tracking
 * - Shows current status and time on shift
 * 
 * Props:
 * - compact: boolean - Use smaller layout for dashboard embedding
 * - onStatusChange: function - Callback when status changes
 */

const ClockWidget = ({ compact = false, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/clock/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStatus(response.data);
        onStatusChange?.(response.data);
      }
    } catch (err) {
      console.error('Error fetching clock status:', err);
      setError('Could not fetch clock status');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    fetchStatus();
  }, []);

  // Get user's location
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  // Clock In
  const handleClockIn = async () => {
    setActionLoading(true);
    setError(null);
    
    try {
      let locationData = {};
      
      // Try to get location if required or available
      if (status?.location_required || status?.salon_location_set) {
        try {
          const loc = await getLocation();
          locationData = loc;
          setLocation(loc);
        } catch (locErr) {
          if (status?.location_required) {
            setLocationError('Location required. Please enable location services.');
            setActionLoading(false);
            return;
          }
          console.warn('Could not get location:', locErr);
        }
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/clock/in', {
        ...locationData,
        method: 'app'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        await fetchStatus();
      }
    } catch (err) {
      console.error('Error clocking in:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    setActionLoading(true);
    setError(null);
    
    try {
      let locationData = {};
      try {
        const loc = await getLocation();
        locationData = loc;
      } catch (locErr) {
        console.warn('Could not get location for clock out:', locErr);
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/clock/out', {
        ...locationData
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        // Show summary briefly
        setStatus(prev => ({
          ...prev,
          clocked_in: false,
          last_shift_summary: response.data
        }));
        
        setTimeout(() => fetchStatus(), 3000);
      }
    } catch (err) {
      console.error('Error clocking out:', err);
      setError(err.response?.data?.error || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  // Start Break
  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/clock/break/start', {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };

  // End Break
  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/clock/break/end', {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={compact ? styles.compactContainer : styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  // Not enabled
  if (!status?.clock_enabled) {
    return null;
  }

  // Compact view for dashboard
  if (compact) {
    return (
      <div style={styles.compactContainer}>
        {status?.clocked_in ? (
          <div style={styles.compactClockedIn}>
            <div style={styles.compactStatus}>
              <span style={styles.statusDot}></span>
              {status.on_break ? 'On Break' : 'Clocked In'}
            </div>
            <div style={styles.compactTime}>{status.time_on_shift}</div>
            <button
              onClick={handleClockOut}
              disabled={actionLoading}
              style={styles.compactButtonOut}
            >
              {actionLoading ? '...' : 'Clock Out'}
            </button>
          </div>
        ) : (
          <button
            onClick={handleClockIn}
            disabled={actionLoading}
            style={styles.compactButtonIn}
          >
            {actionLoading ? 'Clocking In...' : '🕐 Clock In'}
          </button>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div style={styles.container}>
      {/* Current Time */}
      <div style={styles.currentTime}>
        {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={styles.currentDate}>
        {currentTime.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>

      {/* Error Message */}
      {(error || locationError) && (
        <div style={styles.error}>
          {error || locationError}
        </div>
      )}

      {/* Not Clocked In */}
      {!status?.clocked_in && (
        <div style={styles.notClockedIn}>
          {status?.scheduled_shift?.start && (
            <div style={styles.scheduledShift}>
              <span style={styles.scheduleLabel}>Today's shift:</span>
              <span style={styles.scheduleTime}>
                {status.scheduled_shift.start} - {status.scheduled_shift.end}
              </span>
            </div>
          )}
          
          <button
            onClick={handleClockIn}
            disabled={actionLoading}
            style={styles.clockInButton}
          >
            {actionLoading ? (
              <span>Clocking In...</span>
            ) : (
              <>
                <span style={styles.buttonIcon}>🕐</span>
                <span>Clock In</span>
              </>
            )}
          </button>

          {status?.location_required && (
            <div style={styles.locationNote}>
              📍 Location verification required
            </div>
          )}
        </div>
      )}

      {/* Clocked In */}
      {status?.clocked_in && (
        <div style={styles.clockedIn}>
          {/* Status Badge */}
          <div style={{
            ...styles.statusBadge,
            backgroundColor: status.on_break ? '#f59e0b' : '#10b981'
          }}>
            {status.on_break ? '☕ On Break' : '✓ Clocked In'}
          </div>

          {/* Time on Shift */}
          <div style={styles.timeOnShift}>
            <div style={styles.timeLabel}>Time on shift</div>
            <div style={styles.timeValue}>{status.time_on_shift}</div>
          </div>

          {/* Shift Info */}
          {status.scheduled_start && (
            <div style={styles.shiftInfo}>
              <span>Scheduled: {status.scheduled_start} - {status.scheduled_end}</span>
              {status.late_arrival && (
                <span style={styles.lateBadge}>Late arrival</span>
              )}
            </div>
          )}

          {/* Break Info */}
          {status.break_minutes > 0 && (
            <div style={styles.breakInfo}>
              Break taken: {status.break_minutes} mins
            </div>
          )}

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            {status.on_break ? (
              <button
                onClick={handleEndBreak}
                disabled={actionLoading}
                style={styles.endBreakButton}
              >
                {actionLoading ? 'Ending...' : 'End Break'}
              </button>
            ) : (
              <button
                onClick={handleStartBreak}
                disabled={actionLoading}
                style={styles.breakButton}
              >
                {actionLoading ? '...' : '☕ Take Break'}
              </button>
            )}

            <button
              onClick={handleClockOut}
              disabled={actionLoading || status.on_break}
              style={{
                ...styles.clockOutButton,
                opacity: status.on_break ? 0.5 : 1
              }}
            >
              {actionLoading ? 'Clocking Out...' : 'Clock Out'}
            </button>
          </div>

          {status.on_break && (
            <div style={styles.breakNote}>
              End your break before clocking out
            </div>
          )}
        </div>
      )}

      {/* Last Shift Summary (shown briefly after clock out) */}
      {status?.last_shift_summary && (
        <div style={styles.summary}>
          <div style={styles.summaryTitle}>Shift Complete! ✓</div>
          <div style={styles.summaryDetail}>
            Total worked: {status.last_shift_summary.total_worked}
          </div>
          {status.last_shift_summary.overtime_minutes > 0 && (
            <div style={styles.summaryDetail}>
              Overtime: {Math.round(status.last_shift_summary.overtime_minutes / 60 * 10) / 10}h
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: '360px',
    margin: '0 auto'
  },
  compactContainer: {
    padding: '12px'
  },
  loading: {
    color: '#64748b',
    padding: '20px'
  },
  currentTime: {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1e293b',
    letterSpacing: '-1px'
  },
  currentDate: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px'
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  notClockedIn: {
    marginTop: '16px'
  },
  scheduledShift: {
    backgroundColor: '#f8fafc',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  scheduleLabel: {
    fontSize: '12px',
    color: '#64748b',
    display: 'block',
    marginBottom: '4px'
  },
  scheduleTime: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#334155'
  },
  clockInButton: {
    width: '100%',
    padding: '20px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'transform 0.1s, background-color 0.2s'
  },
  buttonIcon: {
    fontSize: '24px'
  },
  locationNote: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#64748b'
  },
  clockedIn: {
    marginTop: '16px'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '20px',
    color: 'white',
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '20px'
  },
  timeOnShift: {
    marginBottom: '16px'
  },
  timeLabel: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '4px'
  },
  timeValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1e293b'
  },
  shiftInfo: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  lateBadge: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: '600'
  },
  breakInfo: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '16px'
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px'
  },
  breakButton: {
    flex: 1,
    padding: '14px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  endBreakButton: {
    flex: 1,
    padding: '14px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#fef3c7',
    color: '#b45309',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  clockOutButton: {
    flex: 1,
    padding: '14px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  breakNote: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#f59e0b'
  },
  summary: {
    backgroundColor: '#f0fdf4',
    padding: '20px',
    borderRadius: '12px',
    marginTop: '16px'
  },
  summaryTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#166534',
    marginBottom: '8px'
  },
  summaryDetail: {
    fontSize: '14px',
    color: '#166534'
  },
  // Compact styles
  compactClockedIn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  compactStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#10b981'
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    animation: 'pulse 2s infinite'
  },
  compactTime: {
    fontSize: '14px',
    color: '#64748b',
    flex: 1
  },
  compactButtonOut: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  compactButtonIn: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  }
};

export default ClockWidget;
